import React, { useState, useMemo } from 'react';
import { Form, Input } from 'antd';
import styled from 'styled-components';
import type { FormValues } from '../services/types';
import type { IScheduleBookingDateItem } from 'services/types/shop';
import moment from 'utils/moment';
import shopSelectors from 'services/selectors/shop';
import TimeSlots from '../widgets/TimeSlots';
import DatePicker from 'components/DatePicker';

interface FormSectionPanelProps {
  reasonTags: string[];
}

const getScheduleTimeSlots = (schedule: IScheduleBookingDateItem | null) => {
  const start = moment(schedule?.startTime, 'HH:mm');
  const end = moment(schedule?.endTime, 'HH:mm');

  const slots: string[] = [];
  let current = start.clone();
  while (current.isSameOrBefore(end)) {
    slots.push(current.format('hh:mm A'));
    current = current.clone().add(15, 'minute');
  }
  return slots;
};

const FormSectionPanel: React.FC<FormSectionPanelProps> = ({
  reasonTags,
}) => {

  const form = Form.useFormInstance<FormValues>();
  const [timeType, setTimeType] = useState<'start' | 'end'>('start');

  const selectedDate = form.getFieldValue('date');
  const scheduleKey = selectedDate?.format?.('ddd') || 'Mon';
  const schedule = shopSelectors.scheduleBooking()?.[scheduleKey] || null;
  const generatedTimeSlots = useMemo(() => getScheduleTimeSlots(schedule), [schedule]);

  const handleToggleTag = (tag: string) => {
    form.setFieldValue('reason', tag);
  };

  return (
    <FormPanel>
      <FormSection>
        <SectionTitle>Date</SectionTitle>
        <DateRow>
          <Form.Item name="date" noStyle>
            <DatePicker />
          </Form.Item>
        </DateRow>
      </FormSection>
      <FormSection>
        <SectionTitle>Timeline</SectionTitle>
        <Form.Item shouldUpdate noStyle>
          {({getFieldValue}) => {
            const selectedTimeStart = getFieldValue('selectedTimeStart');
            const selectedTimeEnd = getFieldValue('selectedTimeEnd');
            return (
              <TimelineSummary>
                <TimeBadge $active={timeType === 'start'} onClick={() => setTimeType('start')}>
                  {selectedTimeStart}
                </TimeBadge>
                <TimeBadge $active={timeType === 'end'} onClick={() => setTimeType('end')}>
                  {selectedTimeEnd}
                </TimeBadge>
              </TimelineSummary>
            );
          }}
        </Form.Item>
        <Form.Item 
          name={timeType === 'start' ? 'selectedTimeStart' : 'selectedTimeEnd'} 
          rules={timeType === 'end' ? [
            {
              validator: (_, value) => {
                const selectedTimeStart = form.getFieldValue('selectedTimeStart');
                if (!value || !selectedTimeStart) return Promise.resolve();
                
                const startMoment = moment(selectedTimeStart, 'hh:mm A');
                const endMoment = moment(value, 'hh:mm A');
                
                if (endMoment.isAfter(startMoment)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('End time must be after start time'));
              },
            },
          ] : undefined}
        >
          <TimeSlots
            slots={generatedTimeSlots}
          />
        </Form.Item>
      </FormSection>

      <FormSection>
        <SectionTitle>Reason</SectionTitle>
        <Form.Item name="reason" noStyle>
          <Input.TextArea placeholder="Please enter..." rows={3} />
        </Form.Item>
        <TagsWrap>
          {reasonTags.map((tag: string) => (
            <ReasonTag
              key={tag}
              onClick={() => handleToggleTag(tag)}
            >
              {tag}
            </ReasonTag>
          ))}
        </TagsWrap>
      </FormSection>
    </FormPanel>
  );
};

export default FormSectionPanel;

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px 20px;
  gap: 14px;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
`;

const FormSection = styled.div``;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #7b7b7b;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .ant-picker { flex: 1; border-radius: 8px; font-family: 'Poppins', sans-serif; }
  .ant-row {
    width: 100%;
  }
`;

const TimelineSummary = styled.div`
  display: flex;
  gap: 8px;
`;

const TimeBadge = styled.div<{ $active: boolean }>`
  flex: 1;
  background: ${({ $active }) => ($active ? '#e6f4ff' : '#f5f5f5')};
  border-radius: 8px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#1677ff' : '#1a1a1a')};
  border: 1px solid ${({ $active }) => ($active ? '#1677ff' : '#e0e0e0')};
  height: 46px;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const ReasonTag = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  background: #e6f4ff;
  color: #1677ff;
  font-size: 12px;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
`;
